import { ChallengeStatusEnum } from '../utils/common'
import API from './api'
import { Billing, Plan, PriceType } from './plans'

export interface Usage {
  totalLimit: string
  usedCount: string
  available: boolean
  isDone: boolean
}

export interface UserInfoType {
  id: number
  email: string
  name: string
  termsAccepted: boolean
  youtubeAccepted: boolean
  youtubeAccount: YoutubeAccount
  youtubeChannel: YoutubeChannel[]
}
export interface UserAffiliatePoint {
  validUntil: string
  daysLeft: string
  hoursLeft: string
  minutesLeft: string
  secondsLeft: string
  affiliateCode: string
  affiliateDescription: string
  affiliateImage: string
  daysValid: number
  type: string
  name: string
  description: string
  point: number
  pointRate: string
  pointReasonType: string
  pointExpired: string
  pointExpiredDay: number
  planId: number
  planSubscriptionType: string
  planDiscountAmount: string
  planDiscountRate: string
}

export interface UserAffiliatePlan {
  validUntil: string
  daysLeft: string
  hoursLeft: string
  minutesLeft: string
  secondsLeft: string
  affiliateCode: string
  affiliateDescription: string
  affiliateImage: string
  daysValid: number
  type: string
  name: string
  description: string
  point: number
  pointRate: string
  pointReasonType: string
  pointExpired: string
  pointExpiredDay: string
  planId: number
  planSubscriptionType: string
  planDiscountAmount: string
  planDiscountRate: string
}

export interface FastCutShortUsageSummaryType {
  ready: string
  complete: string
}

export interface FastCutUsageSummaryType {
  ready: string
  complete: string
  upload: string
}

export interface Me {
  userType?: string
  subscriptions?: {
    planName: string
    userId: number
    planId: number
    startDate: string
    monthStart: string
    endDate: string
    subscriptionType: string
    daysLeft: number
  }
  challenge?: { id: number; status: ChallengeStatusEnum }
  userInfo?: UserInfoType
  alreadyBenefit?: any
  usage?: Usage
  inviteCode: string
  registInviteCode?: string
  experiencedUser: boolean
  fastCutUsageSummary: FastCutUsageSummaryType[]
  fastCutShortUsageSummary: FastCutShortUsageSummaryType[]
  invitePointRate: number
  affiliate: {
    point: UserAffiliatePoint
    plan: any[]
  }
  plan: UserAffiliatePlan[]
  couponBenefit?: {
    until: string
    daysLeft: number
    couponDescription: string
  }
  billing: Billing
  fastClickLink: {
    fastClickLink: string
    productCount: number
  }
}

type NextActionTypes = {
  name: string
  code: string
  api: string
}
export type enableSubscriptions = {
  name: '구독 해지 취소'
  code: 'ENABLE_SUBSCRIPTIONS'
  api: '/billing/billing-methods-enable'
}
export type ReSubscriptions = {
  name: '구독 재개'
  code: 'RE_SUBSCRIPTIONS'
  api: '/payment/brand-pay-transaction'
}

export interface UserSubscriptionInfo {
  subscriptions?: {
    id: number
    planName: string
    price: string
    userId: number
    planId: number
    planPriceId: number
    startDate: string
    endDate: string
    subscriptionType: string
    daysLeft: number
    subscriptionTypeLabel: string
    nextBillingDate: string
    nextAmount: {
      price: number
      vat: number
    }
  }
  lastSubscriptions?: {
    id: number
    planName: string
    price: string
    userId: number
    planId: number
    planPriceId: number
    subscriptionTypeLabel: string
    startDate: string
    endDate: string
    subscriptionType: string
    daysLeft: number
    nextBillingDate: string
    nextAmount: {
      price: number
      vat: number
    }
  }
  customerKey: string
  nextAction: NextActionTypes
}

export interface UserPlan {
  subscriptions: {
    planName: string
    userId: number
    planId: number
    startDate: string
    endDate: string
    subscriptionType: string
    daysLeft: number
    subscriptionTypeLabel: string
    currentMonth: string
    monthStart?: string
    monthEnd?: string
    round: number
  }
  usage: {
    totalLimit: string
    usedCount: string
    available: number
    isDone: boolean
  }
  planPrice: PriceType
  planDetail: Plan
  billing: Billing
}

export interface FastCutUser {
  id: number
  createdAt: string
  updatedAt: string
  deletedAt: string
  profileId: string
  etc: string
  email: string
  name: string
  sns: string
  termsAccepted: boolean
  youtubeAccepted: boolean
  youtubeChannelName: string
  youtubeChannelUrl: string
}

interface YoutubeChannel {
  channelId: string
  youtubeAuthorityFlag: boolean
  advancedFlag: boolean
  autoUploadFlag: boolean
}
interface YoutubeAccount {
  id: number
  youtubeChannelId: string
  email: string
  youtubeChannelName: string
  youtubeChannelUrl: string
  youtubeCustomUrl: string
  youtubeStudioUrl: string
}
export interface FastCutProfile {
  id: number
  email: string
  name: string
  termsAccepted: boolean
}

export async function getProfile() {
  try {
    const data: FastCutProfile = await API.get('/fast-cut-user/profile', {})
    return data
  } catch (error) {
    // 오류 처리
    throw error
  }
}

export async function getMe(): Promise<Me> {
  try {
    return await API.get('/fast-cut-user/me', {})
  } catch (error) {
    // 오류 처리
    throw error
  }
}

export async function getMePlan(): Promise<UserPlan> {
  try {
    return await API.get('/fast-cut-user/me/plan', {})
  } catch (error) {
    // 오류 처리
    throw error
  }
}

export async function updateYoutubeAuth(data: any) {
  try {
    const { youtube } = data
    return await API.put('fast-cut-user/youtube', youtube[0])
  } catch (error) {
    // 오류 처리
    throw error
  }
}

export async function logOut(data: any) {
  try {
    return await API.post(
      '/fast-cut-user/logout',
      {},
      { withCredentials: true }
    )
  } catch (error) {
    // 오류 처리
    throw error
  }
}

export async function deleteYoutubeAccount(data: any) {
  try {
    return await API.delete('/fast-cut-user/youtube/account/channel', {
      data,
    })
  } catch (error) {
    // 오류 처리
    throw error
  }
}

export async function acceptedTerms(acceptedTerms: boolean) {
  try {
    const result = await API.post('/fast-cut-user/terms', { acceptedTerms })
    return result
  } catch (error) {
    // 오류 처리
    throw error
  }
}

export async function registInviteCode(data: any) {
  try {
    return await API.post('/fast-cut-user/regist-invite-code', data)
  } catch (error) {
    // 오류 처리
    throw error
  }
}

export async function getUserSubscriptionInfo(): Promise<UserSubscriptionInfo> {
  try {
    return await API.get('/fast-cut-user/me/billing')
  } catch (error) {
    // 오류 처리
    throw error
  }
}

export async function getVoiceCloningTicket(): Promise<boolean> {
  try {
    return await API.get('/user-voice/ticket-usable')
  } catch (error) {
    // 오류 처리
    throw error
  }
}
